import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireAdmin, RequireStaff } from '@/components/auth/require-role';
import { defaultPathForRole, useSession } from '@/components/auth/user-provider';
import { AppShell } from '@/components/layout/app-shell';
import { AccessBlockedPage } from '@/pages/access-blocked';
import { AccountLandingPage } from '@/pages/account-landing';
import { AdminAuditLogPage } from '@/pages/admin/audit-log';
import { AdminHomePage } from '@/pages/admin/home';
import { AdminLayout } from '@/pages/admin/layout';
import { AdminUsersPage } from '@/pages/admin/users';
import { AuditLogPage } from '@/pages/audit-log';
import { ApprovedNumbersPage } from '@/pages/approved-numbers';
import { AwaitingAccessPage } from '@/pages/awaiting-access';
import { DashboardPage } from '@/pages/dashboard';
import { EventHistoryPage } from '@/pages/event-history';
import { EventRegisterPage } from '@/pages/event-register';
import { EventWorkspacePage } from '@/pages/event-workspace';
import { EventsPage } from '@/pages/events';
import { HomePage } from '@/pages/home';
import { SettingsAppearancePage } from '@/pages/settings/appearance';
import { SettingsAttendancePage } from '@/pages/settings/attendance';
import { SettingsGeneralPage } from '@/pages/settings/general';
import { SettingsLayout } from '@/pages/settings/layout';
import { StudentsPage } from '@/pages/students';
import { VolunteersPage } from '@/pages/volunteers';

/** Send signed-in users to the landing that matches their role. */
function RoleHomeRedirect() {
  const { session } = useSession();
  if (session.status !== 'signedIn') {
    return <Navigate to="/" replace />;
  }
  return (
    <Navigate to={defaultPathForRole(session.user.role, session.user.isActive)} replace />
  );
}

export function App() {
  return (
    <Routes>
      {/* Public marketing homepage — no AppShell, no auth requirement */}
      <Route path="/" element={<HomePage />} />

      <Route element={<AppShell />}>
        {/* Role-specific landings (simplified chrome in AppShell) */}
        <Route path="awaiting-access" element={<AwaitingAccessPage />} />
        <Route path="access-blocked" element={<AccessBlockedPage />} />
        <Route path="account" element={<AccountLandingPage />} />

        {/* Convenience: /app → role home */}
        <Route path="app" element={<RoleHomeRedirect />} />

        {/* Staff operations — admin + teacher */}
        <Route element={<RequireStaff />}>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="events/:id" element={<EventWorkspacePage />} />
          <Route path="events/:id/history" element={<EventHistoryPage />} />
          <Route path="events/:id/register" element={<EventRegisterPage />} />
          <Route path="students" element={<StudentsPage />} />
          <Route path="volunteers" element={<VolunteersPage />} />
          <Route path="audit" element={<AuditLogPage />} />
        </Route>

        {/* Legacy settings admin URLs → /admin */}
        <Route path="settings/users" element={<Navigate to="/admin/users" replace />} />
        <Route path="settings/audit-log" element={<Navigate to="/admin/audit-log" replace />} />

        {/* Admin-only — single sidebar entry, sub-nav inside AdminLayout */}
        <Route element={<RequireAdmin />}>
          <Route element={<AdminLayout />}>
            <Route path="admin" element={<AdminHomePage />} />
            <Route path="admin/users" element={<AdminUsersPage />} />
            <Route path="admin/audit-log" element={<AdminAuditLogPage />} />
            <Route path="approved-numbers" element={<ApprovedNumbersPage />} />

            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<SettingsGeneralPage />} />
              <Route path="appearance" element={<SettingsAppearancePage />} />
              <Route path="attendance" element={<SettingsAttendancePage />} />
            </Route>
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
